// scanner-fp: `useCallback` deps intentionally omit `api` (module singleton), setters (stable), and local vars. See /app/SECURITY_SCANNER_IGNORE.md §4.
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Send, Inbox as InboxIcon, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import AttachmentComposer, { AttachmentList } from "@/components/AttachmentComposer";

/** Internal team chat — 1-on-1 messages between staff. */
export default function Inbox() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [body, setBody] = useState("");
  const [msgFiles, setMsgFiles] = useState([]);

  const loadTeam = useCallback(async () => {
    const r = await api.get("/crm/teammates");
    setTeam(r.data.filter(m => m.id !== user?.id));
  }, [user]);
  const loadInbox = useCallback(async () => {
    const r = await api.get("/crm/messages/inbox");
    setInbox(r.data);
  }, []);
  useEffect(() => { loadTeam(); loadInbox(); }, [loadTeam, loadInbox]);

  const loadThread = useCallback(async (uid) => {
    const r = await api.get(`/crm/messages/thread/${uid}`);
    setThread(r.data);
  }, []);

  useEffect(() => {
    if (!selected) return;
    loadThread(selected.id);
    const t = setInterval(() => loadThread(selected.id), 4000);
    return () => clearInterval(t);
  }, [selected, loadThread]);

  const send = async () => {
    if ((!body.trim() && msgFiles.length === 0) || !selected) return;
    await api.post("/crm/messages", {
      to_user_id: selected.id,
      body,
      file_ids: msgFiles.map((f) => f.id),
    });
    setBody("");
    setMsgFiles([]);
    loadThread(selected.id);
  };

  const unreadFrom = (id) => inbox.filter(m => m.from_user_id === id && !m.read).length;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[60vh]">
      <div className="md:col-span-1 rounded-xl border border-border bg-card p-2 max-h-[70vh] overflow-y-auto">
        <div className="px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          <InboxIcon className="w-3.5 h-3.5" /> Teammates ({team.length})
        </div>
        {team.map(m => {
          const unread = unreadFrom(m.id);
          return (
            <button
              key={m.id}
              onClick={() => setSelected(m)}
              data-testid={`inbox-teammate-${m.id}`}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between ${
                selected?.id === m.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"
              }`}
            >
              <div>
                <div className="font-medium">{m.name}</div>
                <div className="text-[10px] uppercase opacity-70">{m.role}</div>
              </div>
              {unread > 0 && <Badge variant="secondary">{unread}</Badge>}
            </button>
          );
        })}
        {team.length === 0 && <div className="text-xs text-muted-foreground p-3">No teammates yet.</div>}
      </div>

      <div className="md:col-span-2 rounded-xl border border-border bg-card p-4 flex flex-col">
        {selected ? (
          <>
            <div className="border-b border-border pb-2 mb-3">
              <div className="font-medium">{selected.name}</div>
              <div className="text-xs text-muted-foreground uppercase">{selected.role}</div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 max-h-[50vh]" data-testid="inbox-thread">
              {thread.map(m => {
                const mine = m.from_user_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                      mine ? "bg-primary text-primary-foreground rounded-tr-sm" : "bg-muted rounded-tl-sm"
                    }`}>
                      {m.body && <div>{m.body}</div>}
                      {m.files && m.files.length > 0 && <AttachmentList files={m.files} testId={`inbox-msg-${m.id}-files`} />}
                      <div className={`text-[10px] mt-1 opacity-70`}>{new Date(m.created_at).toLocaleTimeString()}</div>
                    </div>
                  </div>
                );
              })}
              {thread.length === 0 && <div className="text-sm text-muted-foreground text-center py-10">Start the conversation.</div>}
            </div>
            <div className="mt-3 space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Message…"
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
                  data-testid="inbox-input"
                />
                <Button onClick={send} data-testid="inbox-send-btn" disabled={!body.trim() && msgFiles.length === 0}><Send className="w-4 h-4" /></Button>
              </div>
              <AttachmentComposer files={msgFiles} onChange={setMsgFiles} compact testId="inbox-attach" />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
            <MessageSquare className="w-4 h-4 mr-2" /> Select a teammate to start chatting.
          </div>
        )}
      </div>
    </div>
  );
}
